import React, {useCallback, useEffect, useState} from 'react';
import {IonContent, IonFooter, IonModal, IonPage, createGesture} from '@ionic/react';
import {useHistory} from 'react-router-dom';
import Nav from './Nav';
import {ApiService} from "../api/ApiService";
import {PushNotification} from "../api/model/PushNotification";
import {sidnavStore, SidenavState} from "../api/SidenavStore";
import HeroChrome from '../components/HeroChrome';
import {BellIcon, CashIcon, DocIcon, FuelIcon, LoadIcon, MessageIcon, SafetyIcon} from '../components/icons';
import {UserUtils} from '../utils/UserUtils';
import { useLogPageVisit } from '../utils/logPageVisit';
import { logUserAction } from '../utils/logUserAction';

type NotificationKind = 'load' | 'safety' | 'pay' | 'fuel' | 'docs' | 'message';

function normalizeNotificationKind(type: string | undefined | null): NotificationKind {
    const t = (type || '').toLowerCase();
    if (t.includes('load') || t.includes('preplan') || t.includes('haul')) return 'load';
    if (t.includes('safety') || t.includes('bonus') || t.includes('pretrip')) return 'safety';
    if (t.includes('pay') || t.includes('check') || t.includes('payroll') || t.includes('advance')) return 'pay';
    if (t.includes('fuel') || t.includes('mpg') || t.includes('mileage')) return 'fuel';
    if (t.includes('doc') || t.includes('paper') || t.includes('transflo')) return 'docs';
    return 'message';
}

function formatNotificationTime(sent: string): string {
    if (!sent) return '';
    const cleaned = sent.replace('T', ' ').replace('Z', '').replace(/\.\d+$/, '').trim();
    const date = new Date(cleaned.includes(' ') ? cleaned.replace(' ', 'T') : cleaned);
    if (Number.isNaN(date.getTime())) {
        return cleaned.slice(0, -14).substr(5) + '-' + cleaned.substr(0, 4);
    }
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfThatDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const dayDiff = Math.round((startOfToday.getTime() - startOfThatDay.getTime()) / 86400000);
    if (dayDiff === 0) return date.toLocaleTimeString('en-US', {hour: 'numeric', minute: '2-digit'});
    if (dayDiff === 1) return 'Yesterday';
    return date.toLocaleDateString('en-US', {month: 'short', day: 'numeric'});
}

function previewNotificationBody(body: string, max = 90): string {
    if (!body) return '';
    const trimmed = body.trim();
    return trimmed.length > max ? `${trimmed.slice(0, max)}...` : trimmed;
}

const ACCENT = '#99CC00';
const DANGER = '#B23B34';
const BLUE = '#3E6FE0';

function TypeIcon({kind}: {kind: NotificationKind}) {
    switch (kind) {
        case 'load': return <LoadIcon color={ACCENT} size={17} />;
        case 'safety': return <SafetyIcon color={DANGER} size={17} />;
        case 'pay': return <CashIcon color={ACCENT} size={17} />;
        case 'fuel': return <FuelIcon color={ACCENT} size={17} />;
        case 'docs': return <DocIcon color={ACCENT} size={17} />;
        case 'message':
        default: return <MessageIcon color={BLUE} size={17} />;
    }
}

function iconToneClass(kind: NotificationKind): string {
    if (kind === 'safety') return ' notifications-row__icon--danger';
    if (kind === 'message') return ' notifications-row__icon--message';
    return '';
}

function NotificationHub() {
    useLogPageVisit('LP Notifications', 'User viewed their notifications');
    const history = useHistory();
    const {close}: {close: () => void} = sidnavStore((state: SidenavState) => ({open: state.open, close: state.close}));
    const {toggleSidenav}: {toggleSidenav: () => void} = sidnavStore((state: SidenavState) => ({toggleSidenav: state.toggle}));

    const ref = React.useRef<HTMLDivElement>(null);

    const [mounted, setMounted] = useState(false);
    const [msgCount, setMsgCount] = useState(0);
    const [notificationArray, setNotificationArray] = useState<PushNotification[]>([]);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState<PushNotification | null>(null);

    useEffect(() => {
        const id = requestAnimationFrame(() => {
            setTimeout(() => setMounted(true), 30);
        });
        return () => cancelAnimationFrame(id);
    }, []);

    useEffect(() => {
        gestureInit();
    }, []);

    const gestureInit = () => {
        const card = ref.current;
        if (card) {
            const gesture = createGesture({
                el: card,
                gestureName: 'swipeMenu',
                onMove: () => {},
                onEnd: () => { toggleSidenav(); }
            });
            gesture.enable();
        }
    };

    const refreshNotifications = useCallback(() => {
        setLoading(true);
        return ApiService.getPushNotifications('notificationHub')
            .then(it => setNotificationArray(it))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        refreshNotifications();

        ApiService.chatConversationGet('notificationHub', {}).then((it: any) => {
            if (!Array.isArray(it)) return;
            let count = 0;
            for (let i = 0; i < it.length; i++) {
                if (it[i]?.lastMessage?.read !== 1) count += 1;
            }
            setMsgCount(count);
        }).catch(() => {});
    }, [refreshNotifications]);

    function handleMode() {
        history.push('/home?search=true');
    }

    const sendRead = (id: number) => {
        logUserAction('LP Notification Opened', 'User opened a notification');
        ApiService.readPushNotification("notificationHub", {id}).then(() => {
            setNotificationArray(prev => prev.map(n => (n.id === id ? {...n, read: 1} : n)));
        });
    }

    const openNotification = (item: PushNotification) => {
        setActive(item);
        if (item.read !== 1) sendRead(item.id);
        else logUserAction('LP Notification Opened', 'User opened a notification');
    }

    const unreadCount = notificationArray.filter(n => n.read !== 1).length;
    const notificationCount = !UserUtils.isAdmin() && !UserUtils.isDriverManager() && !UserUtils.isInspector() ? unreadCount : 0;

    return (
        <IonPage>
            <IonModal isOpen={!!active} className="notifications-detail-modal" onDidDismiss={() => setActive(null)}>
                {active ? (
                    <div className="notifications-detail-modal__backdrop" onClick={() => setActive(null)}>
                        <div className="notifications-detail" onClick={e => e.stopPropagation()}>
                            <button type="button" className="notifications-detail__close" onClick={() => setActive(null)}>Close</button>
                            <h2 className="notifications-detail__title home-display">{active.title}</h2>
                            <p className="notifications-detail__body">{active.message}</p>
                        </div>
                    </div>
                ) : null}
            </IonModal>

            <IonContent fullscreen className="design-a-content design-a-content--light">
                <div ref={ref} className={`design-a${mounted ? ' design-a--mounted' : ''}`} onClick={() => close()}>
                    <div className="home-hero">
                        <HeroChrome
                            onMenuClick={() => toggleSidenav()}
                            msgCount={msgCount}
                            notificationCount={notificationCount}
                        />

                        <div className="notifications-hero">
                            <div className="notifications-hero__copy">
                                <div className="notifications-hero__eyebrow">Notifications</div>
                                <div className="notifications-hero__title home-display">{unreadCount} new</div>
                            </div>
                            <div className="notifications-hero__badge" aria-hidden="true">
                                <BellIcon color="#ffffff" size={22} />
                            </div>
                        </div>
                    </div>

                    <div className="home-sheet notifications-sheet">
                        {loading && notificationArray.length === 0 ? (
                            <div className="notifications-empty">Loading…</div>
                        ) : null}

                        {!loading && notificationArray.length === 0 ? (
                            <div className="notifications-empty">
                                <div className="notifications-empty__icon" aria-hidden="true">
                                    <BellIcon size={22} />
                                </div>
                                <strong>No Notifications</strong>
                            </div>
                        ) : null}

                        {notificationArray.length > 0 ? (
                            <div className="notifications-list">
                                {notificationArray.map((item, i) => {
                                    const kind = normalizeNotificationKind(item.type);
                                    const unread = item.read !== 1;
                                    return (
                                        <button
                                            type="button"
                                            key={item.id}
                                            className={`notifications-row${unread ? ' notifications-row--unread' : ''}`}
                                            onClick={() => openNotification(item)}
                                            style={{['--notifications-row-delay' as string]: `${0.3 + i * 0.05}s`}}
                                        >
                                            <div className={`notifications-row__icon${iconToneClass(kind)}`}>
                                                <TypeIcon kind={kind} />
                                            </div>
                                            <div className="notifications-row__main">
                                                <div className="notifications-row__top">
                                                    <div className="notifications-row__title">{item.title || 'Notification'}</div>
                                                    <div className="notifications-row__time">{formatNotificationTime(item.sent)}</div>
                                                </div>
                                                <div className="notifications-row__body">{previewNotificationBody(item.message)}</div>
                                            </div>
                                            {unread ? <span className="notifications-row__dot" aria-hidden="true" /> : null}
                                        </button>
                                    );
                                })}
                            </div>
                        ) : null}
                    </div>
                </div>
            </IonContent>
            <IonFooter>
                <Nav pageName="NotificationHub" onSearch={handleMode} />
            </IonFooter>
        </IonPage>
    );
}

export default NotificationHub;
